import React, { useState } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { 
  BarChart3, 
  Users, 
  BookOpen, 
  Settings, 
  Bell, 
  Search,
  Menu,
  X,
  Home,
  Calendar,
  FileText,
  Award,
  Shield,
  HelpCircle,
  LogOut,
  ChevronDown,
  Zap,
  Eye,
  UserCheck
} from 'lucide-react';

interface SidebarItem {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  path: string;
  permission?: string;
  badge?: string;
  children?: SidebarItem[];
}

export function AdminLayout() {
  const { user, logout, hasPermission } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>(['dashboard']);

  // Role-based sidebar configuration
  const getSidebarItems = (): SidebarItem[] => {
    const baseItems: SidebarItem[] = [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: Home,
        path: '/admin',
        permission: 'view_analytics'
      }
    ];

    // Admin and Manager get full access
    if (user?.role === 'admin' || user?.role === 'manager') {
      return [
        ...baseItems,
        {
          id: 'analytics',
          label: 'Analytics',
          icon: BarChart3,
          path: '/admin/analytics',
          permission: 'view_analytics',
          badge: 'New'
        },
        {
          id: 'users',
          label: 'User Management',
          icon: Users,
          path: '/admin/users',
          permission: 'view_users'
        },
        {
          id: 'courses',
          label: 'Course Management',
          icon: BookOpen,
          path: '/admin/courses',
          permission: 'view_courses',
          children: [
            {
              id: 'course-builder',
              label: 'Course Builder',
              icon: Zap,
              path: '/admin/courses/builder',
              permission: 'create_courses'
            },
            {
              id: 'course-library',
              label: 'Course Library',
              icon: BookOpen,
              path: '/admin/courses/library',
              permission: 'view_courses'
            }
          ]
        },
        {
          id: 'certificates',
          label: 'Certificates',
          icon: Award,
          path: '/admin/certificates',
          permission: 'manage_certificates'
        },
        {
          id: 'reports',
          label: 'Reports',
          icon: FileText,
          path: '/admin/reports',
          permission: 'view_analytics'
        },
        {
          id: 'scheduling',
          label: 'Scheduling',
          icon: Calendar,
          path: '/admin/scheduling',
          permission: 'view_users'
        },
        {
          id: 'settings',
          label: 'Settings',
          icon: Settings,
          path: '/admin/settings',
          permission: 'manage_system'
        }
      ];
    }

    // Trainers get limited access
    if (user?.role === 'trainer') {
      return [
        ...baseItems,
        {
          id: 'analytics',
          label: 'Training Analytics',
          icon: BarChart3,
          path: '/admin/analytics',
          permission: 'view_analytics'
        },
        {
          id: 'courses',
          label: 'Course Management',
          icon: BookOpen,
          path: '/admin/courses',
          permission: 'view_courses',
          children: [
            {
              id: 'course-builder',
              label: 'Course Builder',
              icon: Zap,
              path: '/admin/courses/builder',
              permission: 'create_courses'
            },
            {
              id: 'course-library',
              label: 'Course Library',
              icon: BookOpen,
              path: '/admin/courses/library',
              permission: 'view_courses'
            }
          ]
        },
        {
          id: 'certificates',
          label: 'Certificates',
          icon: Award,
          path: '/admin/certificates',
          permission: 'manage_certificates'
        },
        {
          id: 'reports',
          label: 'Training Reports',
          icon: FileText,
          path: '/admin/reports',
          permission: 'view_analytics'
        }
      ];
    }

    return baseItems;
  };

  const sidebarItems = getSidebarItems();
  const filteredSidebarItems = sidebarItems.filter(item => 
    !item.permission || hasPermission(item.permission)
  );

  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const isActiveItem = (path: string) => {
    return location.pathname === path || 
           (path !== '/admin' && location.pathname.startsWith(path));
  };

  const SidebarItem = ({ item, level = 0 }: { item: SidebarItem; level?: number }) => {
    const Icon = item.icon;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.id);
    const isActive = isActiveItem(item.path);

    return (
      <div>
        <Link
          to={item.path}
          onClick={() => {
            if (hasChildren) {
              toggleExpanded(item.id);
            }
            setSidebarOpen(false);
          }}
          className={`
            flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-200
            ${level > 0 ? 'ml-6' : ''}
            ${isActive 
              ? 'bg-blue-600 text-white shadow-lg' 
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }
          `}
        >
          <div className="flex items-center space-x-3">
            <Icon className="w-5 h-5" />
            <span className="font-medium">{item.label}</span>
            {item.badge && (
              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                {item.badge}
              </span>
            )}
          </div>
          {hasChildren && (
            <ChevronDown 
              className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            />
          )}
        </Link>

        {hasChildren && (
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="mt-2 space-y-1">
                  {item.children?.map(child => (
                    <SidebarItem key={child.id} item={child} level={level + 1} />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
    );
  };

  const getRoleDisplayInfo = () => {
    switch (user?.role) {
      case 'admin':
        return { title: 'DVSA Admin', subtitle: 'System Administrator', color: 'bg-red-600' };
      case 'manager':
        return { title: 'DVSA Manager', subtitle: 'Training Manager', color: 'bg-orange-600' };
      case 'trainer':
        return { title: 'DVSA Trainer', subtitle: 'Course Management', color: 'bg-purple-600' };
      default:
        return { title: 'DVSA Admin', subtitle: 'Training Platform', color: 'bg-blue-600' };
    }
  };

  const roleInfo = getRoleDisplayInfo();

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out
        lg:translate-x-0 lg:static lg:inset-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 ${roleInfo.color} rounded-xl flex items-center justify-center`}>
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">{roleInfo.title}</h1>
                <p className="text-xs text-gray-500">{roleInfo.subtitle}</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {filteredSidebarItems.map(item => (
              <SidebarItem key={item.id} item={item} />
            ))}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center space-x-3 p-3 rounded-xl bg-gray-50">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-blue-600">
                  {user?.name.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                <div className="flex items-center">
                  <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
                    user?.role === 'admin' ? 'bg-red-500' :
                    user?.role === 'manager' ? 'bg-orange-500' :
                    user?.role === 'trainer' ? 'bg-purple-500' : 'bg-blue-500'
                  }`}></span>
                  <span className="text-xs text-gray-500 capitalize">{user?.role}</span>
                </div>
              </div>
            </div>
            
            <div className="mt-3 space-y-1">
              {(user?.role === 'admin' || user?.role === 'manager') && (
                <Link
                  to="/learner"
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
                >
                  <Eye className="w-4 h-4" />
                  <span>Learner View</span>
                </Link>
              )}
              
              {user?.role === 'trainer' && (
                <Link
                  to="/"
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
                >
                  <BookOpen className="w-4 h-4" />
                  <span>My Learning</span>
                </Link>
              )}
              
              <Link
                to="/admin/profile"
                className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <Settings className="w-4 h-4" />
                <span>Profile Settings</span>
              </Link>
              <button
                onClick={logout}
                className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <LogOut className="w-4 h-4" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:ml-0">
        {/* Top Bar */}
        <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              >
                <Menu className="w-5 h-5" />
              </button>
              
              <div className="hidden md:block">
                <div className="relative">
                  <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search users, courses, reports..."
                    className="pl-10 pr-4 py-2 w-96 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <button className="relative p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>
              
              <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                <HelpCircle className="w-5 h-5" />
              </button>

              <div className="flex items-center space-x-3 pl-4 border-l border-gray-200">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                  <p className="text-xs text-gray-500 capitalize">{user?.role} • {user?.department}</p>
                </div>
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-blue-600">
                    {user?.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}